<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center">
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black opacity-50 modal-wrapper" @click="attemptClose"></div>

    <!-- Modal -->
    <div class="relative bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 z-10 m-4 max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <div>
          <h2 class="text-2xl font-bold text-gray-900">Create Shopping List</h2>
          <p class="text-gray-600 mt-1">Create an empty shopping list with a date range</p>
        </div>
        <button @click="attemptClose" class="text-gray-600 hover:text-gray-800 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Basic Information Form -->
      <ShoppingListBasicForm v-model="formData" :errors="formErrors" :submit-attempted="submitAttempted"
        :loading="creatingShoppingList" @submit="handleSubmit" @cancel="attemptClose" />
    </div>
  </div>

  <!-- Unsaved Changes Dialog -->
  <UnsavedChangesDialog 
    :show="showUnsavedChangesDialog"
    @keep-editing="keepEditing"
    @discard="discardChanges"
  />
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { CREATE_SHOPPING_LIST } from '../../graphql/mutations'
import { showError } from '../../utils/errorHandling'
import ShoppingListBasicForm from './ShoppingListBasicForm.vue'
import UnsavedChangesDialog from '../UnsavedChangesDialog.vue'
import { vm } from '../../utils/validationMessages'
import dayjs from 'dayjs'

// Props
interface Props {
  show: boolean
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
  close: []
  created: [shoppingList: any]
}>()

// State
const formData = ref({
  title: '',
  start_date: '',
  end_date: '',
})

const formErrors = ref({
  title: '',
  start_date: '',
  end_date: '',
})

const showUnsavedChangesDialog = ref(false)
const submitAttempted = ref(false)

// Mutations
const { mutate: createShoppingList, loading: creatingShoppingList } = useMutation(CREATE_SHOPPING_LIST)

// Computed
const hasUnsavedChanges = computed(() => {
  return formData.value.title.trim() !== '' ||
         formData.value.start_date !== '' ||
         formData.value.end_date !== ''
})

// Methods
const resetForm = () => {
  formData.value = { title: '', start_date: '', end_date: '' }
  formErrors.value = { title: '', start_date: '', end_date: '' }
  submitAttempted.value = false
}

const validateStep1 = () => {
  formErrors.value = { title: '', start_date: '', end_date: '' }
  let isValid = true

  if (!formData.value.title.trim()) {
    formErrors.value.title = vm.shoppingListName.required
    isValid = false
  }

  if (!formData.value.start_date) {
    formErrors.value.start_date = vm.startDate.required
    isValid = false
  }

  if (!formData.value.end_date) {
    formErrors.value.end_date = vm.endDate.required
    isValid = false
  }

  if (formData.value.start_date && formData.value.end_date) {
    if (dayjs(formData.value.end_date).isBefore(dayjs(formData.value.start_date))) {
      formErrors.value.end_date = vm.endDate.afterStart
      isValid = false
    }
  }

  return isValid
}

const handleSubmit = async () => {
  submitAttempted.value = true
  if (!validateStep1()) {
    return
  }

  try {
    const result = await createShoppingList({
      title: formData.value.title,
      start_date: dayjs(formData.value.start_date).format('DD/MM/YYYY'),
      end_date: dayjs(formData.value.end_date).format('DD/MM/YYYY')
    })

    emit('created', result?.data?.createShoppingList)
    closeModal()
  } catch (error) {
    showError(error, 'create shopping list')
  }
}

const attemptClose = () => {
  if (hasUnsavedChanges.value) {
    showUnsavedChangesDialog.value = true
  } else {
    closeModal()
  }
}

const closeModal = () => {
  resetForm()
  showUnsavedChangesDialog.value = false
  emit('close')
}

const keepEditing = () => {
  showUnsavedChangesDialog.value = false
}

const discardChanges = () => {
  closeModal()
}

// Watch for modal visibility changes
watch(() => props.show, (show) => {
  if (!show) {
    resetForm()
  }
})
</script>